// AssetMetadataFileRow Component - Named Export Pattern
import React, { useState, useCallback } from 'react';
import { Delete20Regular, Save20Regular } from '@fluentui/react-icons';
import { GenerateButton, LookupSheetButton, AutoLookupSheetButton, HoverTip } from '@repo/ui';
import { MessageBar, MessageBarIntent } from '@fluentui/react-components';
import styled from 'styled-components';
import { useUserStore, useTwinContext } from '@repo/front-end-state';
import Config from '../../common/Config';

/**
 * Delay in milliseconds to wait after persisting data to DGraph before refreshing the 3D scene.
 * This allows time for DGraph to propagate changes across its distributed nodes.
 *
 * Note: 500ms was chosen as a conservative estimate based on typical DGraph propagation times
 * under normal load. Under heavy load, propagation may take longer, which could result in
 * the 3D scene not immediately reflecting the latest changes. If this becomes an issue,
 * consider implementing a retry mechanism or polling strategy that verifies data availability
 * before refreshing, rather than relying on a fixed delay.
 */
const DGRAPH_PROPAGATION_DELAY_MS = 500;

interface AssetMetadataFileRowProps {
  twin: any;
  enabled: boolean;
  reason: string;
  onGenerate: () => void;
  onSave?: () => void;
  feedback?: { intent: MessageBarIntent; message: string }[];
  addAssetMetadataFeedback?: (
    feedback: { intent: MessageBarIntent; message: string },
    clear?: boolean,
  ) => void;
  removeAssetMetadataFeedback?: (index: number) => void;
  isSavingTwin?: boolean;
  isSavingSheet?: boolean;
}

const AssetMetadataFileRow_: React.FC<AssetMetadataFileRowProps> = ({
  twin,
  enabled,
  reason,
  onGenerate,
  onSave,
  feedback,
  addAssetMetadataFeedback,
  removeAssetMetadataFeedback,
  isSavingTwin = false,
  isSavingSheet = false,
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { authMetadata } = useUserStore();
  const { twinBId, setTwinNeedsRefresh } = useTwinContext();

  const getSheetId = (url?: string) => {
    if (!url) return undefined;
    const match = url.match(/\/d\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : undefined;
  };

  const sheetId = getSheetId(twin?.adminTwin?.google_sheet_url);

  const handleGenerate = () => {
    onGenerate();
    setIsEditMode(false);
  };

  const handleCancel = () => {
    setIsEditMode(false);
  };

  const saveMetadataSheet = useCallback(async () => {
    const accessToken: string | undefined = authMetadata?.tokens?.accessToken;

    if (!accessToken) {
      addAssetMetadataFeedback?.({
        intent: 'error',
        message: 'Missing access token',
      });
      return;
    }

    if (!twinBId) {
      addAssetMetadataFeedback?.({
        intent: 'error',
        message: 'Missing twin ID',
      });
      return;
    }

    const sheetId = getSheetId(twin?.adminTwin?.google_sheet_url || undefined);

    if (!sheetId) {
      addAssetMetadataFeedback?.({
        intent: 'error',
        message: 'No sheet available to save',
      });
      return;
    }

    setIsSaving(true);
    addAssetMetadataFeedback?.(
      {
        intent: 'success',
        message: 'Saving Twin Metadata File data to DGraph...',
      },
      true,
    );

    try {
      const response = await fetch(`${Config.middlewareApi}api/google-sheets/persist-sheet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          sheetId,
          twinBid: twinBId,
        }),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;

        // Provide more specific error messages based on status code
        switch (response.status) {
          case 400:
            errorMessage = 'Invalid request. Please check your data and try again.';
            break;
          case 401:
            errorMessage = 'Authentication failed. Please log in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to save this data.';
            break;
          case 404:
            errorMessage = 'Sheet not found. Please regenerate the metadata file.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = `Failed to save data (${response.status}). Please try again.`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();

      // Check if all entities failed
      if (result.persisted_count === 0 && result.failed_count > 0) {
        addAssetMetadataFeedback?.({
          intent: 'error',
          message: `Failed to persist any entities to DGraph (${result.failed_count} failed)`,
        });
      } else if (result.failed_count > 0) {
        addAssetMetadataFeedback?.({
          intent: 'warning',
          message: `Successfully persisted ${result.persisted_count} entities to DGraph (${result.failed_count} failed)`,
        });
      } else {
        addAssetMetadataFeedback?.({
          intent: 'success',
          message: `Successfully persisted ${result.persisted_count} entities to DGraph`,
        });
      }

      // Trigger refresh of the 3D scene to reflect metadata changes
      // Only refresh if entities were actually persisted and wait a moment for DGraph to propagate
      if (result.success && result.persisted_count > 0) {
        // Add a delay to allow DGraph to propagate changes before querying
        setTimeout(() => {
          setTwinNeedsRefresh(true);
        }, DGRAPH_PROPAGATION_DELAY_MS);
      }

      // Exit edit mode on successful save
      setIsEditMode(false);
    } catch (error) {
      console.error('Error persisting sheet data:', error);
      addAssetMetadataFeedback?.({
        intent: 'error',
        message: 'Failed to persist sheet data to DGraph. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  }, [authMetadata, twinBId, addAssetMetadataFeedback, twin, setTwinNeedsRefresh]);

  const handleSave = () => {
    saveMetadataSheet();
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
        {!twin?.adminTwin?.google_sheet_url && (
          <span style={{ flex: 1, color: '#888', fontSize: '16px', marginRight: 8 }}>
            No metadata file generated
          </span>
        )}

        <div style={{ flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center' }}>
          {isEditMode && (
            <>
              <GenerateButton
                enabled={enabled && !isSavingTwin && !isSavingSheet}
                reason={
                  isSavingTwin
                    ? 'FML import in progress'
                    : isSavingSheet
                      ? 'Saving to Dgraph in progress'
                      : reason
                }
                onClick={handleGenerate}
              />
              <LookupSheetButton
                sheetId={sheetId}
                baseUrl={Config.middlewareApi}
                accessToken={authMetadata?.tokens?.accessToken}
              />
              <AutoLookupSheetButton
                sheetId={sheetId}
                baseUrl={Config.middlewareApi}
                accessToken={authMetadata?.tokens?.accessToken}
                onStart={() =>
                  addAssetMetadataFeedback?.(
                    { intent: 'success', message: 'Auto lookup running...' },
                    true,
                  )
                }
                onProgress={(cur, tot) =>
                  addAssetMetadataFeedback?.(
                    { intent: 'success', message: `Auto lookup running ${cur}/${tot}...` },
                    true,
                  )
                }
                onComplete={updated =>
                  addAssetMetadataFeedback?.(
                    {
                      intent: 'success',
                      message: `Auto lookup complete! ${updated} row(s) updated.`,
                    },
                    true,
                  )
                }
              />
              <HoverTip text={!sheetId ? 'No file available to save' : 'Save file data to DGraph'}>
                <button
                  className="btnSave"
                  type="button"
                  onClick={onSave || handleSave}
                  disabled={isSaving || !sheetId}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: isSaving || !sheetId ? 'not-allowed' : 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: isSaving || !sheetId ? 0.5 : 1,
                  }}
                >
                  <Save20Regular />
                </button>
              </HoverTip>
              <button
                className="btnDelete"
                type="button"
                onClick={handleCancel}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Delete20Regular />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Feedback messages below the row */}
      {feedback && feedback.length > 0 && (
        <div className="inline-feedback" style={{ marginTop: '8px' }}>
          {feedback.map((feedbackItem, i) => (
            <MessageBar key={i} intent={feedbackItem.intent}>
              {feedbackItem.message}
              {removeAssetMetadataFeedback && (
                <button
                  onClick={() => removeAssetMetadataFeedback(i)}
                  style={{
                    background: 'none',
                    cursor: 'pointer',
                    height: '16px',
                  }}
                >
                  ×
                </button>
              )}
            </MessageBar>
          ))}
        </div>
      )}
    </>
  );
};

export const AssetMetadataFileRow = styled(AssetMetadataFileRow_)`
  width: 100%;
`;
